import { useEffect, useState } from 'react';
import {
	ERRORS,
	KeyboardEvents,
	Event as PaylinkEvent,
} from '@paylink/constants';
import { ApiService, DataShareService, eventManager } from '@paylink/services';
import { useThemeConfig } from '@paylink/hooks';
import { IPaylinkDetail } from '@paylink/types';
import { RedirectService } from '@paylink/services/Redirect.service';

import { ThemingUtility } from '@shared/utils';

const usePaylinkInit = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [isPaylinkVerifying, setIsPaylinkVerifying] = useState(false);

	const [hasError, setHasError] = useState(false);
	const [phone, setPhone] = useState('');
	const [phoneError, setPhoneError] = useState('');

	const { applyThemeConfig } = useThemeConfig();

	const fetchThemeConfig = async () => {
		try {
			const themeConfig = await ApiService.getThemeConfig();
			return themeConfig;
		} catch (error) {
			console.error('Error fetching theme config:', error);
			throw error;
		}
	};

	const fetchPaylinkDetail = async (): Promise<IPaylinkDetail> => {
		try {
			eventManager.addEvent(PaylinkEvent.FETCH_PAGE_DETAILS_INIT);
			const detail = await ApiService.getPaylinkDetail();
			DataShareService.paylinkDetail = detail;
			eventManager.addEvent(PaylinkEvent.FETCH_PAGE_DETAILS_SUCCESS, {
				value: detail.orderId ?? 'NA',
			});
			setHasError(false);
			return detail;
		} catch (error) {
			setHasError(true);
			eventManager.addEvent(PaylinkEvent.FETCH_PAGE_DETAILS_FAILED);
			console.error('Error fetching paylink detail:', error);
			throw error;
		}
	};
	const initializePage = async () => {
		try {
			setIsLoading(true);

			const [paylinkDetail, themeConfigResult] = await Promise.allSettled([
				fetchPaylinkDetail(),
				fetchThemeConfig(),
			]);

			if (paylinkDetail.status === 'fulfilled') {
				DataShareService.paylinkDetail = paylinkDetail.value;
			}
			if (
				themeConfigResult.status === 'fulfilled' &&
				!ThemingUtility?.currentTheme?.primary
			) {
				applyThemeConfig(themeConfigResult);
			}
		} catch (error) {
			console.error('[PaymentPageInit] Initialization failed:', error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		initializePage();
	}, []);

	useEffect(() => {
		if (!isLoading && !hasError) {
			eventManager.addPageViewEvent(PaylinkEvent.PAGE_LOADED, {
				value: DataShareService?.paylinkDetail?.orderId || 'NA',
				phoneNumberRequired:
					DataShareService?.paylinkDetail?.phoneNumberRequired ?? true,
			});
		}
	}, [isLoading, hasError]);

	const validatePhone = (val: string) => {
		if (!val.trim()) return ERRORS.PHONE_EMPTY;
		if (val.length < 10) return ERRORS.PHONE_INVALID;
		return '';
	};

	const handlePhoneInput = (val: string, e?: Event) => {
		if (!e) return;
		setPhone(val);
		if (phoneError) setPhoneError(validatePhone(val));

		const _val = val.replace(/\D/g, '').slice(0, 10);
		const input = e.target as HTMLInputElement;
		input.value = _val;
		setPhone(_val);
		if (phoneError) setPhoneError(validatePhone(_val));
	};

	const getEventMeta = () => ({
		value: DataShareService?.paylinkDetail?.orderId ?? 'NA',
		name: DataShareService?.paylinkDetail?.merchantName ?? '',
	});

	const submitCustomerDetails = async (phoneNumber: string) => {
		const meta = getEventMeta();

		try {
			eventManager.addEvent(PaylinkEvent.SUBMIT_PAYMENT_DATA_INIT, meta);
			setPhoneError('');
			setIsPaylinkVerifying(true);
			const { redirectUrl } = await ApiService.verifyCustomer({
				...(phoneNumber && { phoneNumber }),
				name: meta.name,
			});
			eventManager.addEvent(PaylinkEvent.SUBMIT_PAYMENT_DATA_SUCCESS, meta);
			RedirectService.redirectTo(redirectUrl);
		} catch {
			setPhoneError(ERRORS.SOMETHING_WENT_WRONG);
			eventManager.addEvent(PaylinkEvent.SUBMIT_PAYMENT_DATA_FAILED, meta);
		} finally {
			setIsPaylinkVerifying(false);
		}
	};

	const handleVerifyPhone = async () => {
		const meta = getEventMeta();
		eventManager.addEvent(PaylinkEvent.VERIFY_BUTTON_CLICKED, meta);

		const err = validatePhone(phone);
		if (err) {
			eventManager.addEvent(PaylinkEvent.FORM_VALIDATION_FAILED, meta);
			setPhoneError(err);
			return;
		}

		await submitCustomerDetails(phone.toString());
	};

	const handleProceed = async () => {
		eventManager.addEvent(PaylinkEvent.PROCEED_BUTTON_CLICKED, getEventMeta());
		await submitCustomerDetails('');
	};

	const handleKeyDown = (e: KeyboardEvent) => {
		if (e.key === KeyboardEvents.ENTER) {
			handleVerifyPhone();
		}
	};

	return {
		isPaylinkVerifying,
		isLoading,
		hasError,
		phone,
		phoneError,
		phoneNumberRequired:
			DataShareService.paylinkDetail?.phoneNumberRequired ?? true,
		paylinkDetail: DataShareService.paylinkDetail ?? {},
		handlePhoneInput,
		handleVerifyPhone,
		handleProceed,
		handleKeyDown,
	};
};

export default usePaylinkInit;
